import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Dna, PenLine, Award, ArrowRight, Check, Brain, Route as RouteIcon, ClipboardList, TrendingUp, CalendarRange, UserRound } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import SmartLink from "@/components/SmartLink";
import FounderCountdown from "@/components/FounderCountdown";

const ROTATING = [
  { icon: Dna, label: "Coach DNA", title: "Your coaching identity, evolving", accent: "from-[#007AFF] to-blue-600" },
  { icon: ClipboardList, label: "Practice Ready", title: "90-min plan generated in seconds", accent: "from-emerald-500 to-emerald-600" },
  { icon: CalendarRange, label: "Season Planner", title: "12 weeks of coaching, mapped out", accent: "from-violet-500 to-violet-600" },
  { icon: UserRound, label: "Player Growth", title: "Narrative stories, not ratings", accent: "from-amber-500 to-amber-600" },
  { icon: TrendingUp, label: "Monthly Report", title: "Growth you can actually feel", accent: "from-rose-500 to-rose-600" },
];

export default function Landing() {
  usePageMeta({
    title: "CoachIQ | AI Coaching Platform for Soccer Coaches",
    description: "CoachIQ is an AI coaching platform that helps soccer coaches plan practices, manage seasons, develop players, analyze games, and improve their coaching.",
    canonicalPath: "/",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "CoachIQ",
        "alternateName": "CoachIQ by UPHAUS Labs",
        "url": "https://uphauslabs.com/",
        "publisher": { "@type": "Organization", "name": "UPHAUS Labs", "url": "https://uphauslabs.com/" },
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CoachIQ",
        "applicationCategory": "SportsApplication",
        "applicationSubCategory": "Soccer coaching software",
        "operatingSystem": "Web",
        "url": "https://uphauslabs.com/",
        "description": "AI coaching platform for soccer coaches — practice planning, season planning, player development, game analysis, and Coach DNA.",
        "publisher": { "@type": "Organization", "name": "UPHAUS Labs", "url": "https://uphauslabs.com/" },
      },
    ],
  });
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(x => (x + 1) % ROTATING.length), 3200); return () => clearInterval(t); }, []);
  const R = ROTATING[tick];

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-[#007AFF] flex items-center justify-center shadow-md shadow-blue-500/20">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-display font-bold text-lg tracking-tight">CoachIQ</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">by UPHAUS Labs</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/features" data-testid="nav-features-link" className="text-sm font-medium text-gray-700 hover:text-gray-900 hidden sm:inline">Features</Link>
            <Link to="/pricing" data-testid="nav-pricing-link" className="text-sm font-medium text-gray-700 hover:text-gray-900 hidden sm:inline">Pricing</Link>
            <Link to="/about" data-testid="nav-about-link" className="text-sm font-medium text-gray-700 hover:text-gray-900 hidden sm:inline">About</Link>
            <Link to="/founding-coach" data-testid="nav-founder-link" className="text-sm font-medium text-amber-600 hover:text-amber-700 hidden sm:inline">Founding Coach</Link>
            <SmartLink to="/login" data-testid="nav-login-link" className="text-sm font-medium text-gray-700 hover:text-gray-900">Sign in</SmartLink>
            <SmartLink to="/signup" data-testid="nav-signup-link" className="bg-[#007AFF] hover:bg-[#0056B3] text-white text-sm font-semibold rounded-full px-5 py-2.5 transition-colors">
              Start Free
            </SmartLink>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 pt-16 md:pt-24 pb-20 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-6" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              UPHAUS Labs · The AI Operating System for Coaches
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight text-gray-900 leading-[1.02]" data-testid="hero-headline">
              Become the coach<br/>your players deserve.
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-lg leading-relaxed" data-testid="hero-sub">
              CoachIQ is an AI coaching platform for soccer coaches. It remembers every practice, every game, and every reflection so you improve all season long.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <SmartLink to="/signup" data-testid="hero-cta-signup" className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#0056B3] text-white font-semibold rounded-full px-6 py-3.5 transition-colors shadow-lg shadow-blue-500/25">
                Start Free — 30 Days <ArrowRight className="w-4 h-4" />
              </SmartLink>
              <Link to="/features" data-testid="hero-cta-explore" className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold rounded-full px-6 py-3.5 transition-colors">
                Explore CoachIQ features
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
              {["30-day free trial · No card required", "Premium access complimentary during Beta", "Grows with your season"].map(x => (
                <span key={x} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> {x}</span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-8 bg-gradient-to-tr from-blue-100 via-white to-emerald-100 rounded-[48px] blur-2xl opacity-70" />
            <div className="relative card-soft p-2 rotate-1 hover:rotate-0 transition-transform duration-500">
              <img
                src="https://images.pexels.com/photos/8941613/pexels-photo-8941613.jpeg"
                alt="Coach with young players"
                className="w-full rounded-2xl object-cover aspect-[4/5]"
              />
            </div>
            <div key={tick} className="absolute -bottom-4 -left-6 card-soft p-4 w-64 hidden md:block animate-slide-up">
              <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white bg-gradient-to-br ${R.accent} rounded-full px-2 py-0.5`}>
                <R.icon className="w-3 h-3" /> {R.label}
              </div>
              <div className="mt-2 font-display font-bold text-gray-900 text-sm leading-tight">{R.title}</div>
              <div className="mt-2 flex gap-1">
                {ROTATING.map((_, i) => <span key={i} className={`h-1 rounded-full transition-all ${i === tick ? "w-6 bg-[#007AFF]" : "w-1 bg-gray-200"}`} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-12" data-testid="why-different">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-[#007AFF]">Why CoachIQ is different</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">Not another chatbot. A season-long coaching partner.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <div className="card-soft p-7 hover:-translate-y-1 transition-transform duration-300">
            <div className="w-12 h-12 rounded-2xl bg-[#007AFF]/10 flex items-center justify-center text-2xl">🧠</div>
            <div className="font-display font-bold text-xl text-gray-900 mt-4">Remembers Everything</div>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">Every game, practice, reflection, Coach DNA update, and player development becomes part of your coaching history.</p>
          </div>
          <div className="card-soft p-7 hover:-translate-y-1 transition-transform duration-300">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-2xl">⚽</div>
            <div className="font-display font-bold text-xl text-gray-900 mt-4">Builds Better Practices</div>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">Generate practices personalized to your team, coaching philosophy, season goals, and previous performances.</p>
          </div>
          <div className="card-soft p-7 hover:-translate-y-1 transition-transform duration-300">
            <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center text-2xl">📈</div>
            <div className="font-display font-bold text-xl text-gray-900 mt-4">Makes You Better</div>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">Coach DNA tracks your coaching evolution and explains WHY every recommendation matters so coaches continue improving.</p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Dna, title: "Coach DNA", desc: "Nine coaching traits that evolve with every game and reflection." },
            { icon: RouteIcon, title: "Coaching Journey", desc: "The full timeline of your season — games, practices, reflections, milestones." },
            { icon: PenLine, title: "Weekly reflection", desc: "One question a week. Your answers shape your Coach DNA and monthly reports." },
            { icon: Brain, title: "Long-term memory", desc: "Recommendations reference last month's focus and this week's growth naturally." },
            { icon: CalendarRange, title: "Season Planner", desc: "Generate a full multi-week roadmap. Evolves as your season unfolds." },
            { icon: Award, title: "Coach Legacy", desc: "Not wins and losses. Lives influenced, moments shared, character built." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="card-soft p-6 hover:-translate-y-1 transition-transform duration-300">
              <div className="w-11 h-11 rounded-2xl bg-[#007AFF]/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#007AFF]" />
              </div>
              <div className="font-display font-bold text-lg text-gray-900">{title}</div>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16" data-testid="uphaus-company-section">
        <div className="rounded-3xl bg-gray-50 border border-gray-100 p-8 md:p-10">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">The company behind CoachIQ</div>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl bg-gray-900 text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
            <div className="font-display font-bold text-2xl tracking-tight">UPHAUS Labs</div>
          </div>
          <p className="mt-3 text-gray-700 leading-relaxed max-w-2xl">
            Building AI that helps coaches spend less time managing and more time coaching.
            <br className="hidden md:block" />
            <span className="text-gray-500">CoachIQ is the first product from UPHAUS Labs.</span>
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to="/about" data-testid="uphaus-about-link" className="text-sm font-semibold text-[#007AFF] hover:underline">About UPHAUS Labs →</Link>
            <a href="https://uphauslabs.com" target="_blank" rel="noopener noreferrer" data-testid="uphaus-external-link" className="text-sm font-semibold text-gray-600 hover:text-gray-900">uphauslabs.com ↗</a>
          </div>
          <div className="mt-6">
            <FounderCountdown variant="compact" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <div className="card-soft p-8 md:p-12 bg-gradient-to-br from-[#007AFF] to-blue-600 text-white relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">Become the coach<br/>your players deserve.</div>
            <div className="mt-3 text-blue-100 max-w-xl mx-auto">CoachIQ becomes more valuable every season because it remembers everything.</div>
            <Link to="/signup" className="mt-6 inline-flex items-center gap-2 bg-white text-[#007AFF] hover:bg-blue-50 font-semibold rounded-full px-6 py-3.5 transition-colors">Start Free — 30 Days <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-100 bg-gray-50/50">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-5 gap-8 text-sm">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-[#007AFF] flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
              <div className="font-display font-bold text-lg">UPHAUS Labs</div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-white bg-emerald-500 rounded px-1 py-0.5">Beta</span>
            </div>
            <div className="text-sm text-gray-600 max-w-sm leading-relaxed">Building AI software that helps coaches spend less time managing and more time coaching.</div>
            <a href="https://uphauslabs.com" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs text-gray-500 hover:text-[#007AFF]" data-testid="footer-uphaus-link">uphauslabs.com</a>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Products</div>
            <div className="space-y-2">
              <Link to="/features" className="block text-gray-700 hover:text-[#007AFF]">CoachIQ</Link>
              <Link to="/pricing" className="block text-gray-700 hover:text-[#007AFF]">Pricing</Link>
              <Link to="/founding-coach" className="block text-gray-700 hover:text-[#007AFF]">Founding Coach</Link>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Company</div>
            <div className="space-y-2">
              <Link to="/about" className="block text-gray-700 hover:text-[#007AFF]">About</Link>
              <Link to="/blog" className="block text-gray-700 hover:text-[#007AFF]">Blog</Link>
              <span className="block text-gray-400 cursor-not-allowed">Careers <span className="text-xs">(Coming soon)</span></span>
              <Link to="/contact" className="block text-gray-700 hover:text-[#007AFF]">Contact</Link>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Resources</div>
            <div className="space-y-2">
              <Link to="/help-public" className="block text-gray-700 hover:text-[#007AFF]">Help Center</Link>
              <Link to="/resources" className="block text-gray-700 hover:text-[#007AFF]">Coach Resources</Link>
              <Link to="/release-notes" className="block text-gray-700 hover:text-[#007AFF]">Release Notes</Link>
            </div>
            <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 mt-6">Legal</div>
            <div className="space-y-2">
              <Link to="/privacy" className="block text-gray-700 hover:text-[#007AFF]">Privacy</Link>
              <Link to="/terms" className="block text-gray-700 hover:text-[#007AFF]">Terms</Link>
              <Link to="/cookies" className="block text-gray-700 hover:text-[#007AFF]">Cookies</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100 py-5 text-center text-xs text-gray-500">© 2026 UPHAUS Labs. All rights reserved. · CoachIQ is a product of UPHAUS Labs · v0.9 Beta</div>
      </footer>
    </div>
  );
}
